import React, { useMemo } from 'react';
import { useEventWebsiteGuestHeadCsrQuery } from './GuestSiteHeadCsr.graphql.generated';
import { useExtractEventPhoto } from '@shared/utils/eventPhoto';
import { Root } from './GuestSiteHead';
import { useMasthead } from '@apps/guest/packages/layout-engine/layouts/hooks/useMasthead/useMasthead';

/**
 * Client-side-only GuestSiteHead for the non-SSR fallback path.
 * Makes its own lightweight query for SEO meta tags.
 * Used when the websiteFullySsrGuestSite feature flag is disabled.
 */
export const GuestSiteHeadCsr: React.FC<{ eventHandle: string }> = ({ eventHandle }) => {
  const { data } = useEventWebsiteGuestHeadCsrQuery({
    variables: { eventHandle },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: true,
  });

  const event = data?.eventByName;
  // Primitive deps keep the headData memo (and Helmet reconciliation) stable.
  // No eventDesign here — the CSR SEO query skips it (like accentPreloadUrl); Helmet mounts
  // too late on this path for a saved-masthead preload to beat the layout's own CSS fetch.
  const resolvedMasthead = useMasthead();
  const mastheadPreloadUrl = resolvedMasthead?.assetUrl ?? null;
  const mastheadMobilePreloadUrl = resolvedMasthead?.mobileAssetUrl ?? null;

  const eventPhoto = useExtractEventPhoto(event);
  const headData = useMemo(() => {
    return event
      ? {
          hideFromSearchEngines: event.settings.hideFromSearchEngines,
          eventDisplayName: event.info.eventDisplayName,
          imageUrl: eventPhoto?.url,
          handle: event.website,
          ownerFirstName: event.info.ownerFirstName,
          ownerLastName: event.info.ownerLastName,
          fianceeFirstName: event.info.fianceeFirstName,
          pageData: event.pages,
          eventType: event.info.eventType,
          accentPreloadUrl: null,
          mastheadPreloadUrl,
          mastheadMobilePreloadUrl,
        }
      : undefined;
  }, [event, mastheadPreloadUrl, mastheadMobilePreloadUrl, eventPhoto]);

  return headData ? <Root {...headData} /> : null;
};
